import { ActionIcon, AppShell, Group, Stack, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { NE_PAGE_COLOR } from "@node-energy/ne-theme";
import { IconX } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { CurrentUser } from "../../utils/backend-types";
import type { SiteName } from "../../utils/enums";
import { Menu } from "../Menu/Menu";
import { NAVBAR_SCROLLBAR_COLOR, NAVBAR_SHADOW } from "../Menu/Menu.constants";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { Footer } from "./Footer";
import { Header } from "./Header";
import "./Layout.scss";
import { useHeaderHeight } from "./useHeaderHeight";

const TESTCLUSTERS = [
  {
    urlPrefix: "fluffy-tiger.testsystem",
    name: "Fluffy Tiger",
    color: "#FFA500"
  },
  {
    urlPrefix: "dreamy-beaver.testsystem",
    name: "Dreamy Beaver",
    color: "#FFA500"
  },
  { urlPrefix: "pre-prod.testsystem", name: "Pre-Prod", color: "#FFA500" },
  {
    urlPrefix: "insane-panini.testsystem",
    name: "Insane Panini",
    color: "#FFA500"
  },
  {
    urlPrefix: "quirky-quokka.testsystem",
    name: "Quirky Quokka",
    color: "#FFA500"
  },
  {
    urlPrefix: "wobbly-wombat.testsystem",
    name: "Wobbly Wombat",
    color: "#FFA500"
  },
  { urlPrefix: "staging", name: "staging", color: "#FFA500" },
  { urlPrefix: "localhost:8000", name: "localhost", color: "neutral" }
];

const url = window.location.href;
export const NEEDS_TESTCLUSTER_WARNING = TESTCLUSTERS.some((cluster) =>
  url.includes(cluster.urlPrefix)
);

export const CLUSTER_ROW_HEIGHT = 35;
export const BASE_HEADER_HEIGHT = 80;
export const BASE_HEADER_NAV_WIDTH = 180;
export const FOOTER_HEIGHT = 60;
export const MAIN_MARGIN = 30;

interface Props {
  currentUser: CurrentUser | undefined;
  siteName: SiteName;
  children: ReactNode;

  toggleShowStaffView: () => void;
}

function Layout({
  children,
  siteName,
  currentUser,
  toggleShowStaffView
}: Props) {
  const { shouldShowStaffView } = useShouldShowStaffView();
  const { HEADER_HEIGHT, LAYOUT_MAIN_CONTENT_MIN_HEIGHT } = useHeaderHeight();
  const [opened, { toggle }] = useDisclosure();

  const { admin_url = "", has_analyzer_license = false } = currentUser || {};

  return (
    <AppShell
      className="Layout"
      header={{ height: HEADER_HEIGHT }}
      navbar={{
        width: BASE_HEADER_NAV_WIDTH,
        breakpoint: "md",
        collapsed: { mobile: !opened }
      }}
      withBorder={false}
    >
      <AppShell.Header>
        {NEEDS_TESTCLUSTER_WARNING && <TestclusterWarningRow />}
        <Header
          currentUser={currentUser}
          height={BASE_HEADER_HEIGHT}
          navbarOpened={opened}
          navbarToggle={toggle}
          siteName={siteName}
          toggleShowStaffView={toggleShowStaffView}
          width={BASE_HEADER_NAV_WIDTH}
        />
      </AppShell.Header>
      <AppShell.Navbar
        bg="secondary.0"
        maw={BASE_HEADER_NAV_WIDTH}
        style={{
          boxShadow: NAVBAR_SHADOW,
          // keep the shadow from bleeding up over the logo in the header
          clipPath: "inset(0 -100px 0 0)"
        }}
      >
        <div
          style={{
            overflowX: "hidden",
            scrollbarColor: `${NAVBAR_SCROLLBAR_COLOR} transparent`,
            scrollbarWidth: "thin"
          }}
        >
          <Menu
            adminUrl={admin_url ?? undefined}
            isStaff={shouldShowStaffView}
            userHasAnalyzerLicense={has_analyzer_license}
          />
        </div>
      </AppShell.Navbar>
      <AppShell.Main bg={NE_PAGE_COLOR}>
        <Stack m={MAIN_MARGIN} mih={LAYOUT_MAIN_CONTENT_MIN_HEIGHT}>
          {children}
        </Stack>
        <Footer height={FOOTER_HEIGHT} siteName={siteName} />
      </AppShell.Main>
    </AppShell>
  );
}

function TestclusterWarningRow() {
  const { testClusterHidden, hideTestClusterWarning } = useHeaderHeight();
  const currentCluster = TESTCLUSTERS.find((cluster) =>
    url.includes(cluster.urlPrefix)
  );

  if (!currentCluster || testClusterHidden) {
    return null;
  }

  return (
    <Group
      align="center"
      bg={currentCluster.color}
      c="white"
      className="TestclusterWarningRow"
      fw={700}
      h={CLUSTER_ROW_HEIGHT}
    >
      <Group flex={1} justify="center">
        {currentCluster.name}
      </Group>
      <ActionIcon
        color="white"
        me="md"
        variant="subtle"
        onClick={hideTestClusterWarning}
      >
        <Tooltip label="Testsystem-Warnung für diese Session verstecken">
          <IconX size={16} />
        </Tooltip>
      </ActionIcon>
    </Group>
  );
}

export { Layout };
